import React, { useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import WithProcessError from 'hocs/WithProcessError';
import PropTypes from 'prop-types';

import Button from 'components/Button/Button';
import FormGroup from 'components/FormGroup/FormGroup';
import FormItem from 'components/FormItem/FormItem';
import IconButton from 'components/IconButton/IconButton';
import Input from 'components/Input/Input';
import Panel from 'components/Panel/Panel';
import PanelHeader from 'components/PanelHeader/PanelHeader';
import Select from 'components/Select/Select';

import Add24 from 'icons/Add24';
import Check24 from 'icons/Check24';
import Close24 from 'icons/Close24';

import useApi from 'utils/api';
import { useAuth } from 'utils/auth';
import { validateName, validateUrl } from 'utils/validate';

export const webhook_methods = [
    { value: 'POST', label: 'POST' },
    { value: 'GET', label: 'GET' },
];

const generateHeaderId = () => `header-${Math.round(Math.random() * 100000)}`;

const MAX_HEADERS_COUNT = 5;

function AppCreateTelegram(props) {
    const api = useApi();
    const auth = useAuth();
    const queryClient = useQueryClient();

    const { panelWidth, onBackClick, onCreated, processError } = props;
    const nameRef = useRef();

    const [method, setMethod] = React.useState('POST');
    const [url, setUrl] = React.useState('');
    const [headers, setHeaders] = React.useState([]);

    const [nameError, setNameError] = React.useState(null);
    const [urlError, setUrlError] = React.useState(null);

    const [loading, setLoading] = React.useState(false);

    function handleMethodChange(value) {
        setMethod(value);
    }

    function getAppName() {
        return nameRef.current?.value.trim();
    }

    /**
     * Webhooks headers.
     */
    function handleHeaderValueChange(id, value) {
        const updatedHeaders = headers.map((header) => {
            if (header.id === id) {
                header.value = value;
            }

            return header;
        });

        setHeaders(updatedHeaders);
    }

    function handleHeaderKeyChange(id, value) {
        const updatedHeaders = headers.map((header) => {
            if (header.id === id) {
                header.key = value;
            }

            return header;
        });

        setHeaders(updatedHeaders);
    }

    function addHeader() {
        if (headers.length >= MAX_HEADERS_COUNT) {
            return;
        }

        const id = generateHeaderId();

        setHeaders((prevHeaders) => [...prevHeaders, { id, key: '', value: '' }]);
    }

    function removeHeader(id) {
        setHeaders((prevHeaders) => prevHeaders.filter((h) => h.id !== id));
    }

    function prepareHeaders() {
        const preparedHeaders = headers.reduce((acc, header) => {
            const { key, value } = header;

            if (key.trim().length > 0 && value.trim().length > 0) {
                acc[key.trim()] = value.trim();
            }

            return acc;
        }, {});

        return preparedHeaders;
    }

    /**
     * Validate creation form fields.
     */
    function validateForm() {
        const name = getAppName();
        const error = validateName(name);
        setNameError(error);

        const urlErr = validateUrl(url);
        setUrlError(urlErr);

        return error || urlErr;
    }

    /**
     * Handle done click and create new application.
     */
    function handleDone() {
        const errors = validateForm();

        if (errors === null) {
            const companyId = auth?.company.id;
            const params = {
                app_name: getAppName(),
                webhook_data: {
                    method,
                    url,
                    headers: prepareHeaders(),
                },
                type: 'webhook',
            };
            setLoading(true);
            api.createApplication(companyId, params)
                .then((result) => {
                    if (result.application && typeof onCreated === 'function') {
                        queryClient.invalidateQueries({ queryKey: ['applications', 'company-' + companyId] });
                        onCreated();
                    }
                })
                .catch((error) => {
                    processError('Не удалось создать приложение', error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }

    return (
        <Panel
            contentStyles={{ minWidth: panelWidth, maxWidth: panelWidth, padding: '0 0.5rem' }}
            header={
                <PanelHeader
                    title={'Новое приложение'}
                    back={true}
                    backIcon={<Close24 />}
                    onBackClick={onBackClick}
                    after={<IconButton loading={loading} icon={<Check24 />} onClick={handleDone} accent={true} />}
                />
            }
            separated={true}
        >
            <FormGroup
                title={'Оповещения через Webhook'}
                description={'Уведомления по оплатам или подпискам в Dispurse будут отправляться по указанному URL.'}
            >
                <FormItem label={'Название вебхука'} required={true} error={nameError}>
                    <Input ref={nameRef} name={'app-name'} type={'text'} placeholder={'Например, «Основная воронка»'} />
                </FormItem>

                <FormItem label={'URL вебхука'} required={true} error={urlError}>
                    <Input
                        name={'webhook-url'}
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        type={'text'}
                        placeholder={'https://example.com/webhook'}
                    />
                </FormItem>

                <FormItem label="Метод запроса">
                    <Select options={webhook_methods} defaultValue={method} onChange={handleMethodChange}></Select>
                </FormItem>
            </FormGroup>

            <FormGroup
                title="Заголовки"
                description="Вы можете добавить HTTP-заголовки, которые будут переданы при вызове Webhook."
            >
                {headers.map((header) => (
                    <FormItem key={header.id} label="Заголовок и значение">
                        <Input
                            placeholder="Authorization"
                            value={header.key}
                            onChange={(e) => handleHeaderKeyChange(header.id, e.target.value)}
                        />
                        <div style={{ height: 8 }} />
                        <Input
                            placeholder="Bearer: 1234"
                            value={header.value}
                            onChange={(e) => handleHeaderValueChange(header.id, e.target.value)}
                        />
                        <div style={{ height: 8 }} />
                        <Button
                            level="secondary"
                            size="s"
                            danger
                            text="Удалить"
                            onClick={() => removeHeader(header.id)}
                        ></Button>
                    </FormItem>
                ))}

                <FormItem>
                    <Button icon={<Add24 />} level="secondary" text="Добавить заголовок" onClick={addHeader} />
                </FormItem>
            </FormGroup>
        </Panel>
    );
}

AppCreateTelegram.propTypes = {
    panelWidth: PropTypes.number,
    onBackClick: PropTypes.func,
    onCreated: PropTypes.func,
    processError: PropTypes.func,
};

export default WithProcessError(AppCreateTelegram);
